/**
 * Juice audio — tiny Web Audio "pluck" SFX layered on important taps.
 *
 * No assets, no library. Two detuned oscillators per pluck for richness.
 * AudioContext is created lazily on first user gesture (browsers block it
 * before that). All ramps end at 0.001, never 0 — exponentialRampToValueAtTime
 * throws on zero.
 */

type Pluck = "tap" | "select" | "success" | "error" | "chime";

const FREQS = {
  tap: 660,
  select: 880,
  success: 1320,
  error: 220,
  chime: 1568, // G6 — completion bell
} as const satisfies Record<Pluck, number>;

const DURS = {
  tap: 0.06,
  select: 0.08,
  success: 0.16,
  error: 0.18,
  chime: 0.55,
} as const satisfies Record<Pluck, number>;

const isPluck = (k: unknown): k is Pluck =>
  typeof k === "string" && k in FREQS;

type Ports = {
  readonly pluck?: { readonly subscribe: (h: (k: string) => void) => void } | undefined;
};

let ctx: AudioContext | null = null;

const ensureCtx = (): AudioContext => {
  if (ctx === null) {
    ctx = new (window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext)();
  }
  return ctx;
};

const playPluck = (kind: Pluck): void => {
  const audio = ensureCtx();
  if (audio.state === "suspended") {
    void audio.resume();
  }

  const freq = FREQS[kind];
  const dur = DURS[kind];
  const t0 = audio.currentTime;

  // Two detuned oscillators for warmth
  for (const detune of [-4, 4] as const) {
    const o = audio.createOscillator();
    const g = audio.createGain();
    o.type = kind === "chime" ? "sine" : "triangle";
    o.frequency.value = freq;
    o.detune.value = detune;

    const gain = kind === "chime" ? 0.14 : 0.08;
    g.gain.setValueAtTime(gain, t0);
    g.gain.exponentialRampToValueAtTime(0.001, t0 + dur);

    o.connect(g).connect(audio.destination);
    o.start(t0);
    o.stop(t0 + dur);
  }
};

export const wireJuiceAudio = (ports: Ports): void => {
  if (ports.pluck === undefined) return;

  // Resume the context on first user interaction so subsequent plucks fire.
  const resumeOnce = (): void => {
    void ensureCtx().resume();
    window.removeEventListener("pointerdown", resumeOnce);
    window.removeEventListener("keydown", resumeOnce);
  };
  window.addEventListener("pointerdown", resumeOnce, { once: true });
  window.addEventListener("keydown", resumeOnce, { once: true });

  ports.pluck.subscribe((kind) => {
    if (!isPluck(kind)) {
      console.warn(`[juice-audio] unknown pluck: ${kind}`);
      return;
    }
    try {
      playPluck(kind);
    } catch (err: unknown) {
      console.warn("[juice-audio] pluck failed", err);
    }
  });
};
